import { SimCall } from '@/native/simCall'
import { reportDeviceDialing, startCall } from '@/services/api/calling.service'
import type { Call, CallPurpose } from '@/types/api/calling'

import { simStorage } from './simStorage'

/** Asks for the phone permissions SIM calling needs; true when calls can be placed. */
export async function ensureSimPermissions(): Promise<boolean> {
  let status = await SimCall.checkPermissions()
  if (status.callPhone !== 'granted' || status.callLog !== 'granted') {
    status = await SimCall.requestPermissions()
  }
  return status.callPhone === 'granted' && status.callLog === 'granted'
}

/**
 * Dials a call the server already created (SIM mode) on this phone: remembers it so the call-log
 * sync can report the result, then reports that dialing started.
 */
export async function dialOnThisPhone(call: Call): Promise<void> {
  if (!call.leadNumber) throw new Error('This call has no number to dial')
  const deviceId = await simStorage.deviceId()
  if (!deviceId) throw new Error('This phone is not registered yet. Reopen the app and try again.')
  if (!(await ensureSimPermissions())) throw new Error('Allow phone and call log access to call from this phone')

  await simStorage.addPending({ callId: call.id, number: call.leadNumber, startedAt: Date.now() })
  await simStorage.addCrmNumber(call.leadNumber)
  const workSim = await simStorage.workSim()
  await SimCall.placeCall({ number: call.leadNumber, phoneAccountId: workSim ?? undefined })
  await reportDeviceDialing(call.id, deviceId)
}

/** "Call from this phone" in the app: create the call on the server, then dial it here. */
export async function startSimCallOnThisPhone(recordId: string, phoneFieldId: string, purpose?: CallPurpose) {
  const deviceId = await simStorage.deviceId()
  if (!deviceId) throw new Error('This phone is not registered yet. Reopen the app and try again.')
  if (!(await ensureSimPermissions())) throw new Error('Allow phone and call log access to call from this phone')
  const call = await startCall({ recordId, phoneFieldId, mode: 'SIM', purpose, dialOnThisDevice: true, deviceId })
  await dialOnThisPhone(call)
  return call
}
